import { create } from 'zustand'
import type { Asset, Clip, Project, Track, TrackKind, User } from '../types'
import {
  clipEnd, clipFromAsset, computeDuration, hasTimedSource, newProject, newTrack, sortTracks,
  subtitleClip, textClip, trackKindFor, uid,
} from '../lib/model'
import type { TextProps } from '../types'

const HISTORY_LIMIT = 150
const EPS = 1e-3

export interface Toast { id: number; text: string; kind: 'info' | 'error' | 'ok' }
export type Dialog = null | 'export' | 'projects' | 'vaideo' | 'voice'
export type LeftTab = 'media' | 'text' | 'subtitles' | 'vaideo'

interface EditorState {
  project: Project
  past: Project[]
  future: Project[]
  dirty: boolean // unsaved to server
  serverId: string | null
  savedAt: number | null
  saving: boolean

  selection: string[]
  playhead: number
  playing: boolean
  pps: number // timeline zoom: pixels per second
  snapping: boolean

  user: User | null
  authChecked: boolean
  toasts: Toast[]
  dialog: Dialog
  leftTab: LeftTab
  busy: string | null

  setProject: (p: Project, opts?: { serverId?: string | null; keepHistory?: boolean }) => void
  mutate: (fn: (p: Project) => void, opts?: { history?: boolean }) => void
  beginGesture: () => void
  undo: () => void
  redo: () => void

  select: (ids: string[]) => void
  toggleSelect: (id: string) => void
  setPlayhead: (t: number) => void
  setPlaying: (v: boolean) => void
  setPps: (v: number) => void
  set: (patch: Partial<EditorState>) => void
  toast: (text: string, kind?: Toast['kind']) => void

  addAsset: (a: Asset) => void
  addAssetClip: (assetId: string, opts?: { trackId?: string; startSec?: number }) => string | null
  addText: (props: Partial<TextProps>, dur?: number) => void
  addSubtitle: (text?: string) => void
  splitAtPlayhead: () => void
  deleteSelected: () => void
  duplicateSelected: () => void
  closeGaps: (trackId?: string) => void
  addTrack: (kind: TrackKind) => void
  removeTrack: (trackId: string) => void
}

function finalize(p: Project) {
  p.tracks = sortTracks(p.tracks)
  p.durationSec = computeDuration(p)
  p.modifiedAt = Date.now()
}

export function overlaps(p: Project, trackId: string, start: number, end: number, exclude: Set<string> = new Set()): boolean {
  return p.clips.some((c) => c.trackId === trackId && !exclude.has(c.id) && c.startSec < end - EPS && clipEnd(c) > start + EPS)
}

/** Track where [start, start+dur) fits: preferred one, else another of that kind, else a new track. */
export function resolveTrack(p: Project, kind: TrackKind, start: number, dur: number, preferred?: string, exclude?: Set<string>): string {
  const candidates = p.tracks.filter((t) => t.kind === kind && !t.locked)
  const pref = candidates.find((t) => t.id === preferred)
  if (pref && !overlaps(p, pref.id, start, start + dur, exclude)) return pref.id
  // Visual overlays go above (search top-down); audio goes below (in order).
  for (const t of candidates) {
    if (!overlaps(p, t.id, start, start + dur, exclude)) return t.id
  }
  const n = p.tracks.filter((t) => t.kind === kind).length + 1
  const tr = newTrack(kind, kind === 'audio' ? `Audio ${n}` : `Capa ${n}`)
  if (kind === 'video') p.tracks.unshift(tr)
  else p.tracks.push(tr)
  return tr.id
}

function mainTrack(p: Project): Track {
  const vids = p.tracks.filter((t) => t.kind === 'video')
  return vids[vids.length - 1]
}

export const useEditor = create<EditorState>((set, get) => ({
  project: newProject(),
  past: [],
  future: [],
  dirty: false,
  serverId: null,
  savedAt: null,
  saving: false,
  selection: [],
  playhead: 0,
  playing: false,
  pps: 60,
  snapping: true,
  user: null,
  authChecked: false,
  toasts: [],
  dialog: null,
  leftTab: 'media',
  busy: null,

  setProject: (p, opts) => {
    finalize(p)
    set({
      project: p,
      past: opts?.keepHistory ? [...get().past, get().project].slice(-HISTORY_LIMIT) : [],
      future: [],
      selection: [],
      playhead: 0,
      playing: false,
      serverId: opts?.serverId !== undefined ? opts.serverId : get().serverId,
      dirty: opts?.keepHistory ? true : false,
    })
  },

  mutate: (fn, opts) => {
    const prev = get().project
    const next = structuredClone(prev)
    fn(next)
    finalize(next)
    const history = opts?.history !== false
    set({
      project: next,
      past: history ? [...get().past, prev].slice(-HISTORY_LIMIT) : get().past,
      future: history ? [] : get().future,
      dirty: true,
    })
  },

  beginGesture: () => {
    set({ past: [...get().past, get().project].slice(-HISTORY_LIMIT), future: [] })
  },

  undo: () => {
    const { past, project, future } = get()
    if (!past.length) return
    const prev = past[past.length - 1]
    const ids = new Set(prev.clips.map((c) => c.id))
    set({ project: prev, past: past.slice(0, -1), future: [project, ...future], dirty: true, selection: get().selection.filter((i) => ids.has(i)) })
  },

  redo: () => {
    const { past, project, future } = get()
    if (!future.length) return
    const next = future[0]
    const ids = new Set(next.clips.map((c) => c.id))
    set({ project: next, past: [...past, project], future: future.slice(1), dirty: true, selection: get().selection.filter((i) => ids.has(i)) })
  },

  select: (ids) => set({ selection: ids }),
  toggleSelect: (id) => {
    const s = get().selection
    set({ selection: s.includes(id) ? s.filter((x) => x !== id) : [...s, id] })
  },
  setPlayhead: (t) => set({ playhead: Math.max(0, t) }),
  setPlaying: (v) => set({ playing: v }),
  setPps: (v) => set({ pps: Math.min(600, Math.max(4, v)) }),
  set: (patch) => set(patch),
  toast: (text, kind = 'info') => {
    const id = Date.now() + Math.random()
    set({ toasts: [...get().toasts, { id, text, kind }] })
    setTimeout(() => set({ toasts: get().toasts.filter((t) => t.id !== id) }), kind === 'error' ? 7000 : 3500)
  },

  addAsset: (a) => get().mutate((p) => {
    if (!p.assets.some((x) => x.id === a.id)) p.assets.unshift(a)
  }, { history: false }),

  addAssetClip: (assetId, opts) => {
    const asset = get().project.assets.find((a) => a.id === assetId)
    if (!asset) return null
    let newId: string | null = null
    get().mutate((p) => {
      const kind = trackKindFor(asset.info.kind)
      let start = opts?.startSec
      let preferred = opts?.trackId
      if (start === undefined) {
        if (kind === 'video' && !preferred) {
          // Default: append to the main track, like CapCut.
          const main = mainTrack(p)
          preferred = main.id
          start = p.clips.filter((c) => c.trackId === main.id).reduce((m, c) => Math.max(m, clipEnd(c)), 0)
          if (asset.info.kind === 'image' && p.clips.some((c) => c.trackId === main.id)) {
            start = get().playhead
            preferred = undefined
          }
        } else start = get().playhead
      }
      const probe = clipFromAsset(asset, '', start)
      const trackId = resolveTrack(p, kind, start, probe.durationSec, preferred)
      probe.trackId = trackId
      p.clips.push(probe)
      newId = probe.id
    })
    if (newId) set({ selection: [newId] })
    return newId
  },

  addText: (props, dur = 3) => {
    let id = ''
    get().mutate((p) => {
      const start = get().playhead
      const c = textClip('', start, props, dur)
      c.trackId = resolveTrack(p, 'video', start, dur, p.tracks.find((t) => t.kind === 'video' && p.clips.some((x) => x.trackId === t.id && x.kind === 'text'))?.id ?? p.tracks[0]?.id)
      if (c.trackId === mainTrack(p).id && p.clips.some((x) => x.trackId === c.trackId && x.kind !== 'text')) {
        // never drop texts onto the main footage track
        const tr = newTrack('video', 'Texto')
        p.tracks.unshift(tr)
        c.trackId = tr.id
      }
      p.clips.push(c)
      id = c.id
    })
    set({ selection: [id] })
  },

  addSubtitle: (text = 'Nuevo subtítulo') => {
    let id = ''
    get().mutate((p) => {
      const start = get().playhead
      let tr = p.tracks.find((t) => t.kind === 'video' && t.name === 'Subtítulos')
      if (!tr) {
        tr = newTrack('video', 'Subtítulos')
        p.tracks.unshift(tr)
      }
      const c = subtitleClip(tr.id, start, 2, text)
      if (overlaps(p, tr.id, start, start + 2)) {
        // shrink to the gap before the next subtitle if possible
        const next = p.clips.filter((x) => x.trackId === tr!.id && x.startSec > start + EPS).sort((a, b) => a.startSec - b.startSec)[0]
        const inside = p.clips.some((x) => x.trackId === tr!.id && x.startSec <= start + EPS && clipEnd(x) > start + EPS)
        if (!inside && next && next.startSec - start > 0.2) c.durationSec = next.startSec - start
        else c.trackId = resolveTrack(p, 'video', start, 2)
      }
      c.sourceOutSec = c.durationSec
      p.clips.push(c)
      id = c.id
    })
    set({ selection: [id] })
  },

  splitAtPlayhead: () => {
    const { playhead, selection, project } = get()
    const locked = new Set(project.tracks.filter((t) => t.locked).map((t) => t.id))
    let targets = project.clips.filter((c) => c.startSec < playhead - 0.02 && clipEnd(c) > playhead + 0.02 && !locked.has(c.trackId))
    if (selection.length) {
      const sel = targets.filter((c) => selection.includes(c.id))
      if (sel.length) targets = sel
    }
    if (!targets.length) {
      get().toast('Coloca el cursor sobre un clip para dividirlo')
      return
    }
    const ids = new Set(targets.map((c) => c.id))
    const newSel: string[] = []
    get().mutate((p) => {
      const added: Clip[] = []
      for (const c of p.clips) {
        if (!ids.has(c.id)) continue
        const leftDur = playhead - c.startSec
        const right: Clip = structuredClone(c)
        right.id = uid(10)
        right.startSec = playhead
        right.durationSec = c.durationSec - leftDur
        right.sourceInSec = c.sourceInSec + leftDur * c.speed
        right.sourceOutSec = c.sourceOutSec
        c.durationSec = leftDur
        c.sourceOutSec = c.sourceInSec + leftDur * c.speed
        if (!hasTimedSource(c)) {
          c.sourceInSec = 0; c.sourceOutSec = c.durationSec
          right.sourceInSec = 0; right.sourceOutSec = right.durationSec
        }
        c.audio.fadeOutSec = 0
        right.audio.fadeInSec = 0
        added.push(right)
        newSel.push(right.id)
      }
      p.clips.push(...added)
    })
    set({ selection: newSel })
  },

  deleteSelected: () => {
    const sel = new Set(get().selection)
    if (!sel.size) return
    const locked = new Set(get().project.tracks.filter((t) => t.locked).map((t) => t.id))
    get().mutate((p) => {
      p.clips = p.clips.filter((c) => !sel.has(c.id) || locked.has(c.trackId))
    })
    set({ selection: [] })
  },

  duplicateSelected: () => {
    const sel = get().selection
    if (!sel.length) return
    const newSel: string[] = []
    get().mutate((p) => {
      for (const id of sel) {
        const c = p.clips.find((x) => x.id === id)
        if (!c) continue
        const d: Clip = structuredClone(c)
        d.id = uid(10)
        d.startSec = clipEnd(c)
        const tr = p.tracks.find((t) => t.id === c.trackId)!
        d.trackId = resolveTrack(p, tr.kind, d.startSec, d.durationSec, c.trackId)
        p.clips.push(d)
        newSel.push(d.id)
      }
    })
    set({ selection: newSel })
  },

  closeGaps: (trackId) => {
    get().mutate((p) => {
      const tracks = trackId ? [trackId] : p.tracks.filter((t) => !t.locked).map((t) => t.id)
      for (const tid of tracks) {
        const cs = p.clips.filter((c) => c.trackId === tid).sort((a, b) => a.startSec - b.startSec)
        let t = 0
        for (const c of cs) {
          c.startSec = t
          t = clipEnd(c)
        }
      }
    })
  },

  addTrack: (kind) => get().mutate((p) => {
    const n = p.tracks.filter((t) => t.kind === kind).length + 1
    const tr = newTrack(kind, kind === 'audio' ? `Audio ${n}` : `Capa ${n}`)
    if (kind === 'video') p.tracks.unshift(tr)
    else p.tracks.push(tr)
  }),

  removeTrack: (trackId) => {
    const p0 = get().project
    const tr = p0.tracks.find((t) => t.id === trackId)
    if (!tr) return
    if (p0.tracks.filter((t) => t.kind === tr.kind).length <= 1) {
      get().toast('Debe quedar al menos una pista de este tipo')
      return
    }
    get().mutate((p) => {
      p.tracks = p.tracks.filter((t) => t.id !== trackId)
      p.clips = p.clips.filter((c) => c.trackId !== trackId)
    })
  },
}))

export function selectedClips(s: { project: Project; selection: string[] }): Clip[] {
  return s.project.clips.filter((c) => s.selection.includes(c.id))
}
